import React, { useState, useEffect } from "react";
import axios from "axios";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import Home from "./components/Home";
import Quotes from "./components/Quotes";
import ClientDetail from "./components/ClientDetail";
import Login from "./Login";
import Navbar from "./Navbar";
import PaymentDetail from "./components/PaymentDetail";
import Homepage from "./components/Homepage";
import VendorQuote from "./components/VendorQuote";
import SiteProgress from "./components/SiteProgress";
import VendorEstimate from "./components/VendorEstimate";
import VendorRateForm from "./components/VendorRateForm";
import Drawings3D from "./components/Drawings3D/Drawings3D";
import PaymentRequestPage from "./components/PaymentRequestPage";
import AfterBooking from "./components/AfterBooking";
import CustomerQuoteBuilder from "./components/CustomerQuoteBuilder";
import SalesDashboard from "./components/SalesDashboard";
import LeadsList from "./components/LeadsList";
import LeadRecord from "./components/LeadRecord";
import AdminMasterItems from "./pages/AdminMasterItems";
import AdminDashboard from "./pages/AdminDashboard";
import UserPage from "./pages/UserPage";
import VendorPO from "./components/VendorPO";
import VendorPODetail from "./components/VendorPODetail";
import RoleManagement from "./pages/RoleManagement";
import RecceModule from "./components/RecceModule";

import Finance from "./Finance/Finance";
import FinanceEntry from "./Finance/FinanceEntry";

import FinanceSalesDashboard from "./Finance/Sales/SalesDashboard";
import SalesMasterPage from "./Finance/Sales/SalesMasterPage";
import QuotationPage from "./Finance/Sales/QuotationPage";
import ProformaInvoicePage from "./Finance/Sales/ProformaInvoicePage";
import InvoicePage from "./Finance/Sales/InvoicePage";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.quote.softvolute.in";

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || null;
  } catch {
    return null;
  }
};

const getStoredRole = () => {
  const user = getStoredUser();

  return (user?.role || localStorage.getItem("userRole") || "").toLowerCase();
};

const ProtectedRoute = ({ allowedRoles, children }) => {
  const token = localStorage.getItem("token");
  const role = getStoredRole();

  if (!token) return <Navigate to="/" replace />;

  if (allowedRoles && !allowedRoles.includes(role)) {
    if (role === "admin") return <Navigate to="/admin-dashboard" replace />;

    if (role === "sales_manager" || role === "marketing") {
      return <Navigate to="/sales-dashboard" replace />;
    }

    return <Navigate to="/" replace />;
  }

  return children;
};

const App = () => {
  const location = useLocation();

  const isVendorPublicPage = location.pathname.startsWith("/vendor-rate-form/");

  const token = localStorage.getItem("token");
  const userRole = getStoredRole();

  const isAdmin = userRole === "admin";
  const isSalesUser = userRole === "sales_manager" || userRole === "marketing";

  const [isAuthenticated, setIsAuthenticated] = useState(!!token);

  const [projects, setProjects] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("projects")) || [];
    } catch {
      return [];
    }
  });

  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(
    localStorage.getItem("selectedProjectId"),
  );

  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);
  const [filterRoute, setFilterRoute] = useState(
    localStorage.getItem("filterRoute") || "/before-booking",
  );

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      setIsSidebarOpen(!mobile);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchProjects = async (projectIdToSelect) => {
    const currentToken = localStorage.getItem("token");
    if (!currentToken) return;

    try {
      const cachedProjects = localStorage.getItem("projects");

      if (cachedProjects) {
        const parsed = JSON.parse(cachedProjects);
        setProjects(parsed);

        if (projectIdToSelect) {
          const match = parsed.find((p) => p._id === projectIdToSelect);
          if (match) setSelectedProject(match);
        }
      }

      const res = await axios.get(`${BASE_URL}/api/projects`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });

      const freshProjects = res.data || [];

      setProjects(freshProjects);
      localStorage.setItem("projects", JSON.stringify(freshProjects));

      if (projectIdToSelect) {
        const match = freshProjects.find((p) => p._id === projectIdToSelect);
        if (match) setSelectedProject(match);
      }
    } catch (err) {
      console.error("Project fetch error:", err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchProjects(localStorage.getItem("selectedProjectId"));
    }
  }, [token]);

  useEffect(() => {
    if (projects.length > 0 && selectedProjectId) {
      const foundProject = projects.find((p) => p._id === selectedProjectId);
      if (foundProject) setSelectedProject(foundProject);
    }
  }, [projects, selectedProjectId]);

  const addProject = async (newProject) => {
    const currentToken = localStorage.getItem("token");

    await axios.post(`${BASE_URL}/api/projects`, newProject, {
      headers: {
        Authorization: `Bearer ${currentToken}`,
      },
    });

    fetchProjects(selectedProjectId);
  };

  const handleProjectSelect = (project, fromRoute) => {
    setSelectedProject(project);
    setSelectedProjectId(project._id);
    setFilterRoute(fromRoute);

    localStorage.setItem("selectedProjectId", project._id);
    localStorage.setItem("filterRoute", fromRoute);

    if (isMobile) setIsSidebarOpen(false);
  };

  const handleLogout = () => {
    localStorage.clear();
    setIsAuthenticated(false);
    setProjects([]);
    setSelectedProject(null);
    setSelectedProjectId(null);
  };

  // ✅ PUBLIC VENDOR FORM - no login, no navbar, no sidebar
  if (isVendorPublicPage) {
    return (
      <Routes>
        <Route path="/vendor-rate-form/:token" element={<VendorRateForm />} />
      </Routes>
    );
  }

  if (!isAuthenticated) {
    return (
      <Login
        onLoginSuccess={() => {
          setIsAuthenticated(true);
          fetchProjects(localStorage.getItem("selectedProjectId"));
        }}
      />
    );
  }

  return (
    <div style={{ fontFamily: "Calibri, sans-serif" }}>
      <Navbar
        onLogout={handleLogout}
        isMobile={isMobile}
        toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          paddingTop: "70px",
          height: "100vh",
          overflow: "hidden",
        }}
      >
        {((!isSalesUser && !isAdmin) ||
          (isAdmin && location.pathname === "/projects")) &&
          location.pathname !== "/" &&
          !location.pathname.toLowerCase().startsWith("/finance") &&
          isSidebarOpen && (
            <div
              style={{
                width: isMobile ? "100%" : "250px",
                backgroundColor: "#fff",
                borderRight: "1px solid #e0e0e0",
                overflowY: "auto",
              }}
            >
              <Sidebar
                projects={projects}
                onSelectProject={handleProjectSelect}
                onAddProject={addProject}
                filterRoute={filterRoute}
                selectedProjectId={selectedProjectId}
              />
            </div>
          )}

        <div
          style={{
            flex: 1,
            padding: isMobile ? "10px" : "20px",
            overflowY: "auto",
            backgroundColor: "#f5f7fa",
          }}
        >
          <Routes>
            <Route
              path="/"
              element={
                isAdmin ? (
                  <Navigate to="/admin-dashboard" replace />
                ) : isSalesUser ? (
                  <Navigate to="/sales-dashboard" replace />
                ) : (
                  <Homepage setSelectedProject={setSelectedProject} />
                )
              }
            />

            <Route
              path="/admin-dashboard"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminDashboard
  onSelectProject={(project) =>
    handleProjectSelect(project, "/after-booking")
  }
/>
                </ProtectedRoute>
              }
            />

            <Route
              path="/roles"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <RoleManagement />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/master-items"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminMasterItems />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin-master-items"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminMasterItems />
                </ProtectedRoute>
              }
            />

            <Route
              path="/projects"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AfterBooking />
                </ProtectedRoute>
              }
            />

            <Route
              path="/users"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <UserPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/sales-dashboard"
              element={
                <ProtectedRoute allowedRoles={["sales_manager", "marketing"]}>
                  <SalesDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/leads"
              element={
                <ProtectedRoute allowedRoles={["sales_manager", "marketing"]}>
                  <LeadsList />
                </ProtectedRoute>
              }
            />

            <Route
              path="/leads/:id"
              element={
                <ProtectedRoute allowedRoles={["sales_manager", "marketing"]}>
                  <LeadRecord />
                </ProtectedRoute>
              }
            />

            <Route path="/before-booking" element={<div>Select project</div>} />

            <Route path="/after-booking" element={<AfterBooking />} />

            <Route
              path="/project"
              element={
                selectedProject ? (
                  <Home selectedProject={selectedProject} />
                ) : null
              }
            />

            <Route
              path="/client-detail"
              element={
                selectedProject ? (
                  <ClientDetail selectedProject={selectedProject} />
                ) : null
              }
            />

            <Route
              path="/quote"
              element={
                selectedProject ? <Quotes project={selectedProject} /> : null
              }
            />

            <Route
              path="/PaymentDetail"
              element={
                selectedProject ? (
                  <PaymentDetail
                    project={selectedProject}
                    refreshProjects={fetchProjects}
                  />
                ) : null
              }
            />

            <Route
              path="/VendorQuote"
              element={
                selectedProject?.hasPayment ? (
                  <VendorQuote project={selectedProject} />
                ) : null
              }
            />

            <Route
              path="/vendor-estimate"
              element={
                selectedProject?.hasPayment ? (
                  <VendorEstimate project={selectedProject} />
                ) : null
              }
            />

            <Route
              path="/SiteProgress"
              element={
                selectedProject?.hasPayment ? (
                  <SiteProgress project={selectedProject} />
                ) : null
              }
            />

            <Route
              path="/recce-module"
              element={
                selectedProject?.hasPayment ? (
                  <RecceModule project={selectedProject} />
                ) : null
              }
            />

            <Route
              path="/3dDrawings"
              element={
                selectedProject?.hasPayment ? (
                  <Drawings3D project={selectedProject} />
                ) : null
              }
            />

            <Route path="/vendor-po" element={<VendorPO />} />

            <Route path="/vendor-po/:vendorId" element={<VendorPODetail />} />

            <Route path="/customer-quote" element={<CustomerQuoteBuilder />} />

            <Route
              path="/payment-request/:projectId"
              element={<PaymentRequestPage />}
            />

           <Route
  path="/Finance"
  element={<Finance selectedProject={selectedProject} />}
>
              <Route index element={<Navigate to="entry" replace />} />

              <Route
                path="entry"
                element={<FinanceEntry selectedProject={selectedProject} />}
              />

              <Route
                path="sales-dashboard"
                element={
                  <FinanceSalesDashboard selectedProject={selectedProject} />
                }
              />

              <Route
                path="sales-master"
                element={<Navigate to="service-master" replace />}
              />

              <Route
                path="sales-master/service-master"
                element={
                  <SalesMasterPage
                    selectedProject={selectedProject}
                    type="services"
                  />
                }
              />

              <Route
                path="sales-master/client-master"
                element={
                  <SalesMasterPage
                    selectedProject={selectedProject}
                    type="clients"
                  />
                }
              />

              <Route
                path="sales-master/bank-detail-master"
                element={
                  <SalesMasterPage
                    selectedProject={selectedProject}
                    type="banks"
                  />
                }
              />

              <Route
                path="sales-master/terms-conditions"
                element={
                  <SalesMasterPage
                    selectedProject={selectedProject}
                    type="terms"
                  />
                }
              />

              <Route
                path="quotations"
                element={<QuotationPage selectedProject={selectedProject} />}
              />

              <Route
                path="proforma-invoices"
                element={
                  <ProformaInvoicePage selectedProject={selectedProject} />
                }
              />

              <Route
                path="invoices"
                element={<InvoicePage selectedProject={selectedProject} />}
              />
            </Route>
            <Route
              path="*"
              element={
                isAdmin ? (
                  <Navigate to="/admin-dashboard" replace />
                ) : isSalesUser ? (
                  <Navigate to="/sales-dashboard" replace />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default App;
